<template>
  <form novalidate class="w-full" @submit="onSubmit">
    <JRow spaceMd="12" spaceLg="12" spaceXl="12" class="mb-20 gap-y-16">
      <JCol md="6" lg="6" xl="4">
        <PublicSuppliersSelectInput
          v-model="supplierCode.value.value"
          :error="supplierCode.errorMessage.value"
          :defaultOptions="defaultPublicSupplier"
          :isEdit="isEdit"
          :supplierType="supplierType"
          required
        />
      </JCol>

      <JCol md="6" lg="6" xl="4">
        <FamilyMembersSelectInput
          v-model="familyNumber.value.value"
          :error="familyNumber.errorMessage.value"
          :defaultOptions="defaultFamilyMember"
          :isEdit="isEdit"
          required
        />
      </JCol>

      <JCol md="6" lg="6" xl="4">
        <JTextInput
          v-model="accountNumber.value.value"
          :error="accountNumber.errorMessage.value"
          :label="$t('profile.accountNumber.label')"
          :placeholder="$t('profile.accountNumber.placeholder')"
          required
        />
      </JCol>

      <JCol md="6" lg="6" xl="4" class="flex items-end md:mt-8">
        <JSwitch
          v-model="includeInBooking.value.value"
          :label="$t('profile.booking.label')"
          class="mb-4 flex-1"
        />
      </JCol>
    </JRow>

    <div class="flex gap-x-20 md:justify-end">
      <JButton
        color="danger"
        variant="outlined"
        size="md"
        :block="isMobile"
        :disabled="isLoadingMutation"
        @click.prevent="cancel"
      >
        {{ $t('words.cancel') }}
      </JButton>
      <JButton
        size="md"
        :block="isMobile"
        :disabled="
          !meta.valid || isLoadingMutation || (props.isEdit && !isChanged)
        "
        :isLoading="isLoadingMutation"
        type="submit"
      >
        {{ $t('words.save') }}
      </JButton>
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed, inject, unref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { boolean, number, object, string } from 'yup'
import { useField, useForm } from 'vee-validate'
import isEqual from 'lodash/isEqual'
import { JButton, JCol, JRow, JSwitch, JTextInput } from '@journey/ui-kit'
import type { Emits, Props } from './MyLoyaltyAccountsFormFields.types'
import { REGEX } from '@/modules/Core/constants'
import type { LoyaltyAccount } from '@/modules/Profile/types/model/loyaltyAccount'
import {
  MY_LOYALTY_ACCOUNTS_FORM_INJECTION_KEY,
  updateProfileSectionSharedStateInjectionKey,
} from '@/modules/Profile/constants'
import useProfileStore from '@/modules/Profile/store'
import useDeviceType from '@/modules/Core/composables/useDevicePlatform'
import PublicSuppliersSelectInput from '@/modules/Profile/components/PublicSuppliersSelectInput/PublicSuppliersSelectInput.vue'
import FamilyMembersSelectInput from '@/modules/Profile/components/FamilyMembersSelectInput/FamilyMembersSelectInput.vue'

const props = withDefaults(defineProps<Props>(), {
  isEdit: false,
})

const emit = defineEmits<Emits>()
const { t } = useI18n()

const { isMobile } = useDeviceType()

const updateProfileSectionSharedState = inject(
  updateProfileSectionSharedStateInjectionKey
)
// VALIDATION
const validationSchema = object({
  supplierCode: string().required(
    t('profile.supplierType.validation.required')
  ),
  familyNumber: number().required(t('profile.accountName.validation.required')),
  accountNumber: string()
    .matches(
      REGEX.ONLY_LETTERS_AND_NUMBERS,
      t('profile.accountNumber.validation.lettersAndNumbersOnly')
    )
    .required(t('profile.accountNumber.validation.required')),
  includeInBooking: boolean(),
})

const { handleSubmit, resetForm, setValues, meta, values } =
  useForm<LoyaltyAccount>({
    keepValuesOnUnmount: true,
    validationSchema,
    initialValues: {
      includeInBooking: true,
      ...props.form,
    },
  })

const defaultPublicSupplier = computed(() => {
  if (!props.isEdit) {
    return []
  }

  return [
    {
      key: values.supplierCode,
      value: values.supplierName,
    },
  ]
})

const defaultFamilyMember = computed(() => {
  if (!props.isEdit) {
    return []
  }

  return [
    {
      key: values.accountNumber,
      value: values.accountName.replace(' ', '-'),
    },
  ]
})

defineExpose({
  resetForm,
})

watch(
  () => props.form,
  (value) => {
    if (value === undefined || Object.values(value).every((v) => v === '')) {
      resetForm()
    } else {
      setValues(value)
    }
  },
  {
    deep: true,
  }
)

// MODELS
const supplierCode = useField<LoyaltyAccount['supplierCode']>('supplierCode')
const familyNumber = useField<LoyaltyAccount['familyNumber']>('familyNumber')
const accountNumber = useField<LoyaltyAccount['accountNumber']>('accountNumber')
const includeInBooking =
  useField<LoyaltyAccount['includeInBooking']>('includeInBooking')

// DISABLED
const isChanged = computed(() => !isEqual(meta.value.initialValues, values))

const profileStore = useProfileStore()

const onSubmit = handleSubmit(async (values) => {
  if (props.isEdit && !isChanged.value) {
    return
  }

  if (!updateProfileSectionSharedState) {
    return
  }

  await updateProfileSectionSharedState.items[
    MY_LOYALTY_ACCOUNTS_FORM_INJECTION_KEY
  ].submit({
    ...values,
    supplierType: props.supplierType,
    isEdit: props.isEdit,
  })

  updateProfileSectionSharedState.setTouched(
    MY_LOYALTY_ACCOUNTS_FORM_INJECTION_KEY,
    false
  )

  emit('submit', values)
})

watch(values, (value) => {
  if (props.isEdit) {
    profileStore.setConfirmationPayload(
      isChanged.value
        ? {
            supplierCode: value.supplierCode,
            supplierType: props.supplierType,
            familyNumber: value.familyNumber,
            accountNumber: value.accountNumber,
            includeInBooking: value.includeInBooking,
            isEdit: props.isEdit,
          }
        : null
    )

    return
  }

  profileStore.setConfirmationPayload(isChanged.value ? value : null)
})

watch([isChanged, () => meta.value.valid], ([changed, valid]) => {
  if (!updateProfileSectionSharedState) {
    return
  }

  updateProfileSectionSharedState.setTouched(
    MY_LOYALTY_ACCOUNTS_FORM_INJECTION_KEY,
    valid && changed
  )
})

// LOADING
const isLoadingMutation = computed(() => {
  if (!updateProfileSectionSharedState) {
    return
  }

  return unref(
    updateProfileSectionSharedState.items[
      MY_LOYALTY_ACCOUNTS_FORM_INJECTION_KEY
    ].isLoading
  )
})

// CANCEL
function cancel() {
  updateProfileSectionSharedState?.setTouched(
    MY_LOYALTY_ACCOUNTS_FORM_INJECTION_KEY,
    false
  )
  emit('cancel')
}
</script>
